// Optimistic single-owner add/remove for one interest, shared by the interest
// detail / slideout owners and the waitlist Owner column.
import React from "react";
import { useMutation } from "@apollo/client";
import { refetchHandles } from "data";
import logger from "services/logger";
import { getApolloErrorMessage } from "utils/apollo";
import { useSnackbar } from "components/haven-design/snackbar";
import {
  QueryInterestProfile_interest_owners as InterestOwner,
  addInterestOwner,
  addInterestOwnerVariables,
  removeInterestOwner,
  removeInterestOwnerVariables,
} from "types/gql-types";
import { OwnerSummary } from "./owners-editor";
import {
  compareOwners,
  ownerLabel,
  ownerNameFrom,
} from "./owner-names";
import { ADD_INTEREST_OWNER, REMOVE_INTEREST_OWNER } from "./owner-queries";

export const toOwnerSummaries = (
  owners: Pick<InterestOwner, "id" | "firstName" | "lastName">[],
): OwnerSummary[] =>
  owners.map(({ id, firstName, lastName }) => ({ id, name: ownerNameFrom(firstName, lastName) }));

// The resolver reports a vanished interest as a bare "not_found" atom.
const NOT_FOUND_MESSAGE = "This application is no longer available.";

const ownerChangeErrorMessage = (err: unknown, fallback: string): string => {
  const message = getApolloErrorMessage(err).trim();
  if (!message) return fallback;
  return message === "not_found" ? NOT_FOUND_MESSAGE : message;
};

type OwnerChange = {
  userId: string;
  mutate: () => Promise<unknown>;
  update: (owners: OwnerSummary[]) => OwnerSummary[];
  successMessage: string;
  errorMessage: string;
  onSuccess?: () => void;
};

/**
 * `applicantName` names the application in an error that lands after the caller
 * moved on, so the user can tell which change failed.
 */
export const useOwnerChanges = (
  interestId: string,
  initialOwners: OwnerSummary[],
  applicantName?: string,
) => {
  // Local owners so an add/remove shows without refetching the whole interest.
  const [owners, setOwners] = React.useState<OwnerSummary[]>(initialOwners);
  // Re-seed when the caller moves to another interest or its query refreshes.
  // The key stands in for initialOwners, whose identity changes every render.
  const initialKey = `${interestId}:${initialOwners.map((owner) => owner.id).join(",")}`;
  const [seededKey, setSeededKey] = React.useState(initialKey);
  const [pendingUserIds, setPendingUserIds] = React.useState<ReadonlySet<string>>(new Set());
  if (seededKey !== initialKey) {
    setSeededKey(initialKey);
    setOwners(initialOwners);
    setPendingUserIds(new Set());
  }

  // A change resolving after the caller moved to another interest (slideout
  // navigation, table cell reuse) or unmounted must not touch what is now on
  // screen, which may list the very same owners. Its error is still shown.
  const interestIdRef = React.useRef<string | null>(interestId);
  React.useEffect(() => {
    interestIdRef.current = interestId;
    return () => {
      interestIdRef.current = null;
    };
  }, [interestId]);

  // Keyed by interest so an in-flight change elsewhere doesn't block this one;
  // the ref blocks a second click landing before the disabled state re-renders.
  const pendingRef = React.useRef<Set<string>>(new Set());

  // useSnackbar reads a context that may be absent (isolated unit tests).
  const snackbar = useSnackbar();
  const notify = (message: string, status: "success" | "error") =>
    snackbar?.openSnackbar?.({ message, status, duration: 3000 });

  const [addOwner] = useMutation<addInterestOwner, addInterestOwnerVariables>(ADD_INTEREST_OWNER);
  const [removeOwner] = useMutation<removeInterestOwner, removeInterestOwnerVariables>(
    REMOVE_INTEREST_OWNER,
  );

  const runOwnerChange = ({
    userId, mutate, update, successMessage, errorMessage, onSuccess,
  }: OwnerChange) => {
    const key = `${interestId}:${userId}`;
    if (pendingRef.current.has(key)) return;
    const requestedFor = interestId;
    const requestedName = applicantName?.trim();
    const isCurrent = () => interestIdRef.current === requestedFor;
    pendingRef.current.add(key);
    setPendingUserIds((prev) => new Set(prev).add(userId));
    mutate()
      .then(() => {
        if (!isCurrent()) return;
        setOwners(update);
        onSuccess?.();
        // The Activity feed is a separate query; refetch it so the audit entry
        // appears without a manual refresh.
        refetchHandles.activityFeed();
        notify(successMessage, "success");
      })
      .catch((err) => {
        logger.sendError(err);
        // The snackbar is app-level, so this reaches the user even after the
        // caller unmounted; otherwise a rejected change would look applied.
        const message = ownerChangeErrorMessage(err, errorMessage);
        notify(isCurrent() || !requestedName ? message : `${requestedName}: ${message}`, "error");
      })
      .finally(() => {
        pendingRef.current.delete(key);
        if (!isCurrent()) return;
        setPendingUserIds((prev) => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      });
  };

  const add = (userId: string, name: string | null, onAdded?: () => void) => {
    const label = ownerLabel(name);
    runOwnerChange({
      userId,
      mutate: () => addOwner({ variables: { interestId, userId } }),
      // Placed where the server will list them, so the first owner shown doesn't
      // jump on the next load.
      update: (prev) => (prev.some((owner) => owner.id === userId)
        ? prev
        : [...prev, { id: userId, name }].sort(compareOwners)),
      successMessage: `Added ${label} as owner.`,
      errorMessage: `Couldn't add ${label} as owner.`,
      onSuccess: onAdded,
    });
  };

  const remove = (userId: string) => {
    const owner = owners.find((candidate) => candidate.id === userId);
    const name = owner ? ownerLabel(owner.name) : "owner";
    runOwnerChange({
      userId,
      mutate: () => removeOwner({ variables: { interestId, userId } }),
      update: (prev) => prev.filter((owner) => owner.id !== userId),
      successMessage: `Removed ${name} as owner.`,
      errorMessage: `Couldn't remove ${name} as owner.`,
    });
  };

  return { owners, pendingUserIds, add, remove };
};
